import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trash, X, Warning, Lightning } from "@phosphor-icons/react";

/**
 * Generic deletion-request button.
 *  - Any logged-in user can REQUEST deletion (kicks off 4-stage approval workflow).
 *  - CEO (ux_tier 5) gets an extra FORCE-DELETE option that bypasses approvals
 *    with the reason logged for audit.
 *
 * Props:
 *   entityType  → key from ENTITY_REGISTRY (case, client_invoice, client, …)
 *   entityId    → record's stable id
 *   entityLabel → short human label (case_code / invoice_number / name)
 *   variant     → "icon" (32×32 ghost button, default) | "link" (text link)
 *   onRequested → called after a successful request/force
 *   className   → optional extra class
 */
export default function DeleteWorkflowButton({
  entityType, entityId, entityLabel, variant = "icon", onRequested, className = "",
}) {
  const { user } = useAuth();
  const isCEO = (user?.ux_tier ?? 0) >= 5;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [force, setForce] = useState(false);
  const [done, setDone] = useState(null); // request_id once submitted

  const reset = () => { setOpen(false); setReason(""); setErr(""); setForce(false); setDone(null); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (reason.trim().length < 4) { setErr("Please provide a reason (≥ 4 characters)."); return; }
    setBusy(true); setErr("");
    try {
      const path = force ? "/deletion/force-delete" : "/deletion/request";
      const { data } = await api.post(path, { entity_type: entityType, entity_id: entityId, reason: reason.trim() });
      setDone(data);
      onRequested?.(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Failed to submit.");
    } finally { setBusy(false); }
  };

  const trigger = variant === "link" ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid={`del-trigger-${entityType}-${entityId}`}
      className={`text-[11px] text-[var(--span-danger)] hover:underline font-mono uppercase tracking-[0.12em] ${className}`}
    >
      Request delete
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid={`del-trigger-${entityType}-${entityId}`}
      title="Request deletion"
      className={`h-7 w-7 inline-flex items-center justify-center rounded border border-[var(--span-border)] text-[var(--span-text-2)] hover:text-[var(--span-danger)] hover:border-[var(--span-danger)] transition-colors ${className}`}
    >
      <Trash size={13} />
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="del-modal">
          <div className="card-flat w-full max-w-[520px]">
            <div className="px-5 py-3.5 border-b border-[var(--span-border)] flex items-center justify-between">
              <div>
                <div className="label-eyebrow text-[var(--span-danger)]">
                  {done ? "Request submitted" : (force ? "Force delete" : "Request deletion")}
                </div>
                <div className="font-display font-bold text-[15px] mt-0.5">
                  {entityType.replace("_", " ")} · <span className="font-mono">{entityLabel || entityId}</span>
                </div>
              </div>
              <button onClick={reset} data-testid="del-close"><X size={16} /></button>
            </div>

            {done ? (
              <div className="px-5 py-5 space-y-3 text-[13px]">
                <div className="flex items-start gap-2 text-[var(--span-success,#10B981)]">
                  <Warning size={16} />
                  <div>
                    {force ? (
                      <>This record was <strong>force-deleted</strong>. It will sit in the Recycle Bin for 2 years before purge.</>
                    ) : (
                      <>Your deletion request has been queued. It now needs <strong>RM → FM → COO → CEO</strong> sign-off.</>
                    )}
                  </div>
                </div>
                <div className="text-[11px] font-mono text-[var(--span-text-2)] uppercase tracking-[0.1em]">
                  Request ID: {done.request_id}
                </div>
                <div className="flex justify-end">
                  <button onClick={reset} className="span-btn">Close</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 py-5 space-y-4">
                <div className="text-[12px] text-[var(--span-text-2)] leading-relaxed">
                  Deletion is a {force ? "ONE-STEP override (CEO authority)" : "4-stage workflow"}: the
                  record stays in the Recycle Bin for {force ? "2 years" : "2 years after final CEO approval"}.
                  Please state the reason — it is permanently audit-logged.
                </div>
                <div>
                  <label className="label-eyebrow block mb-1.5">Reason</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    minLength={4}
                    maxLength={1000}
                    data-testid="del-reason"
                    className="span-input min-h-[88px]"
                    placeholder="Duplicate · superseded · data entry error · GDPR request · etc."
                  />
                </div>
                {isCEO && (
                  <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="del-force-toggle">
                    <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="mt-0.5" />
                    <div className="text-[12px]">
                      <div className="flex items-center gap-1.5 font-semibold text-[var(--span-danger)]">
                        <Lightning size={13} /> Force delete (CEO override)
                      </div>
                      <div className="text-[var(--span-text-2)]">Skip the RM/FM/COO chain. Audit logs will record this as a force action.</div>
                    </div>
                  </label>
                )}
                {err && <div className="text-[12px] text-[var(--span-danger)]" data-testid="del-error">{String(err)}</div>}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button type="button" onClick={reset} className="span-btn span-btn-ghost">Cancel</button>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid={force ? "del-force-submit" : "del-submit"}
                    className="span-btn disabled:opacity-50"
                    style={{ background: "var(--span-danger)", borderColor: "var(--span-danger)" }}
                  >
                    {busy ? "Submitting…" : (force ? "Force delete now" : "Submit request")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
